// Wire shapes and pure helpers for fleet, React Mobile's own PTT service.
// The contract is fleet's docs/voice-client-runbook.md: one Phoenix channel,
// `client`, a full-state `display` push, and binary Opus frames.

export type FleetHolder = { device_id: string; since: number | string; node?: string }

export type FleetDisplayChannel = {
  id: string
  name: string
  kind: 'standard' | 'alert'
  holder: FleetHolder | null
}

export type FleetEndReason = 'released' | 'max_talk' | 'revoked'

export type FleetDisplay = {
  selected: string | null
  language: string
  talking: { channel: string; since: number | string } | null
  ended: { channel: string; reason: FleetEndReason } | null
  channels: FleetDisplayChannel[]
}

export type FleetFrame = {
  seq: number
  epoch: number
  senderId: string
  opus: Uint8Array
}

// `Fleet.Voice.Frame` v1: <<1, seq::32-big, sender_size::8, sender_id, opus>>.
// Returns null for any other version or a truncated frame.
export function parseFrame(buf: ArrayBuffer): FleetFrame | null {
  if (buf.byteLength < 6) return null
  const dv = new DataView(buf)
  if (dv.getUint8(0) !== 1) return null

  const seq = dv.getUint32(1, false)
  const senderSize = dv.getUint8(5)
  const opusStart = 6 + senderSize
  if (buf.byteLength <= opusStart) return null

  const senderId = new TextDecoder().decode(new Uint8Array(buf, 6, senderSize))
  return { seq, epoch: seq >>> 20, senderId, opus: new Uint8Array(buf, opusStart) }
}

// Drops frames already played. Fleet stamps each sender's frames with
// `epoch << 20 | counter`; a new epoch (the sender re-attached) starts over.
export class SeqLedger {
  private bySender = new Map<string, { epoch: number; seen: Set<number>; order: number[] }>()

  constructor(private readonly capacity = 6000) {}

  accept(senderId: string, seq: number): boolean {
    const epoch = seq >>> 20
    let entry = this.bySender.get(senderId)
    if (!entry || entry.epoch !== epoch) {
      entry = { epoch, seen: new Set(), order: [] }
      this.bySender.set(senderId, entry)
    }
    if (entry.seen.has(seq)) return false

    entry.seen.add(seq)
    entry.order.push(seq)
    if (entry.order.length > this.capacity) {
      const oldest = entry.order.shift()
      if (oldest !== undefined) entry.seen.delete(oldest)
    }
    return true
  }

  clear() {
    this.bySender.clear()
  }
}

export type HeldStream = {
  key: string
  channelId: string
  channelName: string
  senderId: string
}

export type DisplayIndex = {
  idByName: Map<string, string>
  nameById: Map<string, string>
  // Lowercase names of every channel the subject is a member of.
  readyNames: string[]
  // Who holds each channel's floor, keyed by holder identity.
  held: Map<string, HeldStream>
}

export function indexDisplay(display: FleetDisplay): DisplayIndex {
  const idByName = new Map<string, string>()
  const nameById = new Map<string, string>()
  const held = new Map<string, HeldStream>()

  for (const ch of display.channels || []) {
    idByName.set(ch.name.toLowerCase(), ch.id)
    nameById.set(ch.id, ch.name)
    if (ch.holder) {
      const key = `${ch.id}|${ch.holder.device_id}|${ch.holder.since}`
      held.set(key, { key, channelId: ch.id, channelName: ch.name, senderId: String(ch.holder.device_id) })
    }
  }

  return { idByName, nameById, readyNames: Array.from(idByName.keys()), held }
}

// Floors taken and released between two displays. Fleet has no per-stream
// start/stop events; the holder appearing and clearing stands in for them.
export function diffHeld(
  prev: Map<string, HeldStream>,
  next: Map<string, HeldStream>,
): { started: HeldStream[]; stopped: HeldStream[] } {
  const started: HeldStream[] = []
  const stopped: HeldStream[] = []
  next.forEach((s, key) => {
    if (!prev.has(key)) started.push(s)
  })
  prev.forEach((s, key) => {
    if (!next.has(key)) stopped.push(s)
  })
  return { started, stopped }
}

// Fleet's ask to move this socket to the org's home, make-before-break
// (fleet's docs/voice-steering.md). `slip` rides on the new socket's first
// connect only; the old socket is closed by the server after `ttl_ms`.
export type FleetSteer = { home: string; slip: string; ttl_ms: number }

const DEFAULT_STEER_TTL_MS = 30_000

export function parseSteer(payload: any): FleetSteer | null {
  const home = typeof payload?.home === 'string' ? payload.home.trim() : ''
  const slip = typeof payload?.slip === 'string' ? payload.slip : ''
  if (!home || !slip) return null
  const ttl = Number(payload?.ttl_ms)
  return { home, slip, ttl_ms: Number.isFinite(ttl) && ttl > 0 ? ttl : DEFAULT_STEER_TTL_MS }
}

// The socket URL a steer's `home` names. A zone (`https://`) or task
// (`wss://`) URL replaces host and path; a bare path (`/device/home-3`)
// keeps the host in use. phoenix.js would resolve a bare path against the
// portal's own host, and appends `/websocket` itself.
export function resolveSteerUrl(home: string, currentUrl: string): string | null {
  let url: URL
  try {
    url = new URL(home, currentUrl)
  } catch {
    return null
  }
  if (url.protocol === 'https:') url.protocol = 'wss:'
  else if (url.protocol === 'http:') url.protocol = 'ws:'
  else if (url.protocol !== 'wss:' && url.protocol !== 'ws:') return null
  url.pathname = url.pathname.replace(/\/websocket\/?$/, '')
  return url.toString()
}

// Milliseconds until a voice token is refetched: `leadMs` before `expiresAt`,
// never sooner than `floorMs` from now.
export function refreshDelayMs(
  expiresAt: string | null | undefined,
  now: number,
  leadMs = 5 * 60_000,
  floorMs = 30_000,
) {
  if (!expiresAt) return null
  const at = Date.parse(expiresAt)
  if (Number.isNaN(at)) return null
  return Math.max(floorMs, at - now - leadMs)
}
